import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  type RowSelectionState,
  type PaginationState,
  type OnChangeFn,
  type ColumnSizingState,
} from '@tanstack/react-table'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import * as s from './Table.css'
import {
  buildDataColumnDefs,
  getInitialColumnVisibility,
  orderColumnDefinitions,
  columnFiltersToChipState,
  chipStateToColumnFilters,
  globalSearchFilterFn,
} from './columnAdapter'
import { createColumnDragEndHandler } from './columnDrag'
import { resolveFilterableColumns } from './filterColumns'
import { ScrollOverflowFades } from './ScrollOverflowFades'
import { useScrollOverflowEdges } from './useScrollOverflowEdges'
import { SortableHeaderCell } from './components/SortableHeaderCell'
import { ColumnsConfigButton, FiltersConfigButton } from './components/ViewConfigMenu'
import { FilterToolbarChip } from './components/FilterToolbarChip'
import { BulkSelectionBar } from './components/BulkSelectionBar'
import { FiltersSheetModal } from '../ListView/FiltersSheetModal'
import type { ColumnDefinition } from './types'
import { getColumnLabel } from './types'
import { TableHeaderIcon } from './components/TableHeaderIcon'
import { getDefaultColumnSizing } from './tablePreferences'
import type { ListViewCTAGroup } from '../ListView/components/ListViewCard'
import type { RowFlashState } from '../ListView/types'
import { resolveRowAccentClass } from './rowAccent'
import { DropdownMenu, MenuItem } from '../DropdownMenu'

export { getColumnLabel } from './types'
export type {
  ColumnDefinition,
  Column,
  ColumnFilterType,
  ColumnFilterOption,
  EntityFilterConfig,
  EntityFilterKind,
  EntityFilterOption,
  EntityFilterPageResult,
  TableHeaderIconName,
  TablePreferencesV1,
  TableTab,
  SelectionAction,
  EntityTableDefinition,
  RowAccent,
} from './types'
export { ROW_ACCENT_CLASS, resolveRowAccentClass } from './rowAccent'
export { useTablePreferences } from './hooks/useTablePreferences'
export {
  loadTablePreferences,
  saveTablePreferences,
  tablePreferencesKey,
} from './tablePreferences'

import type { TableTab, SelectionAction } from './types'
import { useT } from "../../../app/lib/i18n";

// ─── Row action menu ──────────────────────────────────────────────────────────

function getRowCtaBtnClass(variant: string | undefined, s: Record<string, unknown>): string {
  if (variant === 'danger') return s.rowCtaBtnDanger as string
  if (variant === 'secondary') return s.rowCtaBtnSecondary as string
  return s.rowCtaBtnPrimary as string
}

function RowActionMenu({
  rowId, rowLabel, actions }: { rowId: string; rowLabel?: string; actions: SelectionAction[] }) {
  const t = useT();
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={s.actionBtn}
        aria-label={t("shared.table.moreOptions")}
        onClick={e => { e.stopPropagation(); setOpen(o => !o) }}
      >
        <Icon name="more" size={16} />
      </button>
      <DropdownMenu
        anchorEl={anchorRef}
        anchorPos="SE"
        openDirection="SW"
        open={open}
        setOpen={setOpen}
        onChange={v => actions[Number(v)]?.onClick([rowId], rowLabel ? [rowLabel] : undefined)}
        closeOnSelect
      >
        {actions.map((action, i) => (
          <MenuItem key={i} value={String(i)}>
            <span className={action.danger ? s.actionMenuItemDanger : undefined}>
              {action.icon}&ensp;{action.label}
            </span>
          </MenuItem>
        ))}
      </DropdownMenu>
    </>
  )
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TableProps<TRow extends { id: string }> {
  columns: ColumnDefinition<TRow>[]
  rows: TRow[]
  /** Initial load with no rows yet — shows skeleton headers/body. */
  loading?: boolean
  /** Background refetch — keeps stale rows visible with a subtle overlay. */
  fetching?: boolean
  onRowClick?: (row: TRow) => void
  /** Left-edge accent key (see `RowAccent` / `ROW_ACCENT_CLASS`). */
  getRowAccent?: (row: TRow) => string | undefined
  /** @deprecated Use `getRowAccent`. */
  getRowStatus?: (row: TRow) => string
  /** Returns a human-readable label for a row — used to personalise single-item confirm dialogs. */
  getRowLabel?: (row: TRow) => string
  searchPlaceholder?: string
  selectionActions?: SelectionAction[]
  /** Dynamic alternative: called with current selectedIds + rows on each render. Takes precedence over selectionActions. */
  getSelectionActions?: (selectedIds: string[], rows: TRow[]) => SelectionAction[]
  /**
   * Per-row ⋯ menu actions. When set, used instead of `selectionActions` for the
   * trailing actions column (so Edit/Delete can differ per row). Bulk bar still
   * uses selectionActions / getSelectionActions.
   */
  getRowActions?: (row: TRow) => SelectionAction[]
  /** Show checkbox column; defaults to true when selectionActions is set */
  enableSelection?: boolean
  /** When false, at most one row can be selected (TanStack `enableMultiRowSelection`). Default true. */
  enableMultiRowSelection?: boolean
  /** When selection is on, return false to disable the checkbox for a row. */
  getRowCanSelect?: (row: TRow) => boolean
  /** Show trailing ⋯ actions column; default true */
  enableRowActions?: boolean
  tabs?: TableTab[]
  activeTab?: string
  onTabChange?: (value: string) => void
  emptyTitle?: string
  emptySubtitle?: string
  /** Icon in empty state panel */
  emptyIcon?: 'battery' | 'users'
  /**
   * When true, fills the parent container (height and width). Toolbar and pagination
   * stay fixed; only the table body scrolls when content overflows.
   */
  fillContainer?: boolean
  /** Server-driven data; use with QueryTable or wire state handlers manually. */
  manualPagination?: boolean
  manualFiltering?: boolean
  /** Total pages from server when manualPagination (use with rowCount). */
  pageCount?: number
  /** Total rows after server filters when manualPagination. */
  rowCount?: number
  pagination?: PaginationState
  onPaginationChange?: OnChangeFn<PaginationState>
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  columnFilters?: ColumnFiltersState
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>
  globalFilter?: string
  onGlobalFilterChange?: (value: string) => void
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>
  columnOrder?: string[]
  onColumnOrderChange?: OnChangeFn<string[]>
  visibleFilterIds?: string[]
  onVisibleFilterIdsChange?: (ids: string[]) => void
  /** Toolbar filter chips (subset of columns with filterType). */
  filterableColumns?: ColumnDefinition<TRow>[]
  /** All filterable columns for the Filters config menu. */
  allFilterColumns?: ColumnDefinition<TRow>[]
  enableColumnReorder?: boolean
  enableViewConfig?: boolean
  columnSizing?: ColumnSizingState
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>
  /** Extra controls rendered after Columns in the toolbar trail. */
  toolbarEnd?: React.ReactNode
  /** Trailing controls on the filter chip row, right-aligned (e.g. Saved Views). */
  toolbarAbove?: React.ReactNode
  /** Center slot in the pagination footer (e.g. ListView mode TabSwitcher). */
  paginationCenter?: React.ReactNode
  /**
   * Alternate stacked toolbar + footer for narrow containers
   * (full-width search, Filters modal, 3-row pagination).
   */
  narrowChrome?: boolean
  /** Sortable columns for the compact Filters modal (defaults to filterable/sortable cols). */
  sortableColumns?: ColumnDefinition<TRow>[]
  sort?: { sort_by: string; order: 'asc' | 'desc' } | null
  onSortChange?: (sort: { sort_by: string; order: 'asc' | 'desc' }) => void
  /** Rendered below pagination (or below the table when pagination is hidden). */
  footer?: React.ReactNode
  /**
   * When this value changes, row selection is cleared.
   * Pass `contextKey` from QueryTable so selection resets after bulk mutations.
   */
  selectionResetKey?: unknown
  /** Controlled row-selection state (lifted out of Table by ListView). */
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  /** Returns true for rows that should receive a pulsing highlight animation. */
  getRowHighlighted?: (row: TRow) => boolean
  /** Returns CTA groups for a row — each group has optional image, text, and action buttons. */
  getRowCtas?: (row: TRow) => ListViewCTAGroup[] | undefined
  /** Rows currently animating a WS update shimmer. */
  flashingRows?: Map<string, RowFlashState>
}

export { QueryTable } from './QueryTable'
export type { QueryTableProps } from './QueryTable'
export { useListQuery } from './hooks/useListQuery'
export { useTableState } from './hooks/useTableState'
export { tableStateToListParams } from './listParams'

// ─── Row CTA overlay subcomponent ────────────────────────────────────────────

function RowCtaOverlay({
  groups }: { groups: ListViewCTAGroup[] }) {
  const t = useT();
  const [idx, setIdx] = useState(0)
  // Key on serialized IDs to avoid resetting on every render (groups is a new ref each render)
  const groupIds = groups.map((g) => g.id).join('\0')
  const prevIdsRef = useRef(groupIds)
  if (prevIdsRef.current !== groupIds) {
    prevIdsRef.current = groupIds
    if (idx !== 0) setIdx(0)
  }
  const group = groups[idx]
  if (!group) return null

  const advance = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    setIdx((i) => (i + 1) % groups.length)
  }

  return (
    <div className={s.rowCtaOverlay}>
      {group.image && (
        <div
          className={s.rowCtaGroupImage}
          title={group.image.tooltip}
          onClick={group.image.onClick ? (e) => { e.stopPropagation(); group.image!.onClick!() } : undefined}
          style={group.image.onClick ? { cursor: 'pointer' } : undefined}
          role={group.image.onClick ? 'button' : undefined}
          tabIndex={group.image.onClick ? 0 : undefined}
        >
          {group.image.src ? (
            <img src={group.image.src} alt={group.image.tooltip ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : null}
        </div>
      )}
      {group.text && <span className={s.rowCtaText}>{group.text}</span>}
      {group.buttons.map((cta) => (
        <button
          key={cta.id}
          type="button"
          className={getRowCtaBtnClass(cta.variant, s)}
          disabled={cta.disabled}
          onClick={(e) => { e.stopPropagation(); cta.onClick() }}
        >
          {cta.label}
        </button>
      ))}
      {groups.length > 1 && (
        <button type="button" className={s.rowCtaNavigatorChip} onClick={advance} aria-label={t("shared.table.nextCta")}>
          {idx + 1}/{groups.length} ›
        </button>
      )}
    </div>
  )
}

// ─── Icon (minimal inline set) ────────────────────────────────────────────────

function Icon({ name, size = 14 }: { name: string; size?: number }) {
  const p = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  switch (name) {
    case 'search':
      return (
        <svg {...p}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      )
    case 'plus':
      return (
        <svg {...p}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      )
    case 'grid':
      return (
        <svg {...p}>
          <rect x="4" y="4" width="7" height="7" />
          <rect x="13" y="4" width="7" height="7" />
          <rect x="4" y="13" width="7" height="7" />
          <rect x="13" y="13" width="7" height="7" />
        </svg>
      )
    case 'more':
      return (
        <svg {...p}>
          <circle cx="5" cy="12" r="1.4" />
          <circle cx="12" cy="12" r="1.4" />
          <circle cx="19" cy="12" r="1.4" />
        </svg>
      )
    case 'x':
      return (
        <svg {...p}>
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      )
    case 'chevron-left':
      return (
        <svg {...p}>
          <path d="m15 6-6 6 6 6" />
        </svg>
      )
    case 'chevron-right':
      return (
        <svg {...p}>
          <path d="m9 6 6 6-6 6" />
        </svg>
      )
    case 'filter':
      return (
        <svg {...p}>
          <path d="M4 6h16M7 12h10M10 18h4" />
        </svg>
      )
    case 'battery':
      return (
        <svg {...p}>
          <rect x="3" y="8" width="16" height="9" rx="1.5" />
          <path d="M19 11v3M7 10v5M10 10v5M13 10v5" />
        </svg>
      )
    case 'users':
      return (
        <svg {...p}>
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
      )
    case 'download':
      return (
        <svg {...p}>
          <path d="M12 4v12m0 0-4-4m4 4 4-4M5 20h14" />
        </svg>
      )
    case 'trash':
      return (
        <svg {...p}>
          <path d="M4 7h16M10 11v6M14 11v6M6 7l1-3h10l1 3M9 7V5h6v2" />
        </svg>
      )
    default:
      return null
  }
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function Pagination({
  page,
  pageCount,
  pageSize,
  total,
  onPage,
  onPageSize,
  className,
  center,
  compact = false,
}: {
  page: number
  pageCount: number
  pageSize: number
  total: number
  onPage: (p: number) => void
  onPageSize: (n: number) => void
  className?: string
  /** Optional center slot (e.g. ListView mode TabSwitcher). */
  center?: React.ReactNode
  /** Stack into 3 rows for narrow chrome. */
  compact?: boolean
}) {
  const t = useT()
  const pages = useMemo(() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i)
    const out: (number | '…')[] = [0]
    if (page > 2) out.push('…')
    for (let i = Math.max(1, page - 1); i <= Math.min(pageCount - 2, page + 1); i++) out.push(i)
    if (page < pageCount - 3) out.push('…')
    out.push(pageCount - 1)
    return out
  }, [page, pageCount])

  const start = page * pageSize + 1
  const end = Math.min(total, (page + 1) * pageSize)
  const rangeLabel = total === 0 ? '0' : `${start}–${end} of ${total}`

  const pageButtons =
    pageCount > 1 ? (
      <div className={s.pageButtons}>
        <button
          type="button"
          className={s.pageBtn}
          onClick={() => onPage(page - 1)}
          disabled={page === 0}
          aria-label={t("shared.table.previous")}
        >
          <Icon name="chevron-left" size={14} />
        </button>
        {pages.map((p, i) =>
          p === '…' ? (
            <button key={`e${i}`} type="button" className={s.pageBtn} disabled>
              …
            </button>
          ) : (
            <button
              key={p}
              type="button"
              className={`${s.pageBtn}${p === page ? ' is-active' : ''}`}
              onClick={() => onPage(p)}
            >
              {p + 1}
            </button>
          ),
        )}
        <button
          type="button"
          className={s.pageBtn}
          onClick={() => onPage(page + 1)}
          disabled={page >= pageCount - 1}
          aria-label={t("shared.table.next")}
        >
          <Icon name="chevron-right" size={14} />
        </button>
      </div>
    ) : null

  if (compact) {
    return (
      <div className={[s.paginationCompact, className].filter(Boolean).join(' ')}>
        <div className={s.paginationCompactInner}>
          {center ? <div className={s.paginationCompactRow}>{center}</div> : null}
          <div className={s.paginationCompactMeta}>
            <div className={s.paginationInfo} style={{ justifySelf: undefined }}>
              <span>{t("shared.table.rowsPerPage")}</span>
              <select
                className={s.pageSizeSelect}
                value={pageSize}
                onChange={(e) => onPageSize(Number(e.target.value))}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span>{rangeLabel}</span>
            </div>
          </div>
          {pageButtons ? (
            <div className={s.paginationCompactPages}>{pageButtons}</div>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className={[s.pagination, className].filter(Boolean).join(' ')}>
      <div className={s.paginationInfo}>
        <span>{t("shared.table.rowsPerPage")}</span>
        <select
          className={s.pageSizeSelect}
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <span style={{ marginLeft: 8 }}>{rangeLabel}</span>
      </div>
      <div className={s.paginationCenter}>{center}</div>
      <div className={s.paginationEnd}>{pageButtons}</div>
    </div>
  )
}

// ─── Table ────────────────────────────────────────────────────────────────────

export function Table<TRow extends { id: string }>({
  columns,
  rows,
  loading = false,
  fetching = false,
  onRowClick,
  getRowAccent,
  getRowStatus,
  getRowLabel,
  searchPlaceholder = 'Search…',
  selectionActions,
  getSelectionActions,
  getRowActions,
  enableSelection: enableSelectionProp,
  enableMultiRowSelection = true,
  getRowCanSelect,
  enableRowActions = true,
  tabs,
  activeTab,
  onTabChange,
  emptyTitle = 'No results',
  emptySubtitle = 'Try adjusting your filters or search.',
  emptyIcon = 'battery',
  fillContainer = false,
  manualPagination = false,
  manualFiltering = false,
  pageCount: serverPageCount,
  rowCount: serverRowCount,
  pagination: controlledPagination,
  onPaginationChange: onPaginationChangeProp,
  sorting: controlledSorting,
  onSortingChange: onSortingChangeProp,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange: onColumnFiltersChangeProp,
  globalFilter: controlledGlobalFilter,
  onGlobalFilterChange: onGlobalFilterChangeProp,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange: onColumnVisibilityChangeProp,
  columnOrder: controlledColumnOrder,
  onColumnOrderChange: onColumnOrderChangeProp,
  visibleFilterIds: controlledVisibleFilterIds,
  onVisibleFilterIdsChange: onVisibleFilterIdsChangeProp,
  filterableColumns: filterableColumnsProp,
  allFilterColumns: allFilterColumnsProp,
  enableColumnReorder = true,
  enableViewConfig = true,
  columnSizing: controlledColumnSizing,
  onColumnSizingChange: onColumnSizingChangeProp,
  toolbarEnd,
  toolbarAbove,
  paginationCenter,
  narrowChrome = false,
  sortableColumns: sortableColumnsProp,
  sort: sortProp,
  onSortChange: onSortChangeProp,
  footer,
  selectionResetKey,
  rowSelection: controlledRowSelection,
  onRowSelectionChange: onRowSelectionChangeProp,
  getRowHighlighted,
  getRowCtas,
  flashingRows,
}: TableProps<TRow>) {
  const t = useT();
  const showSelection = enableSelectionProp ?? ((selectionActions?.length ?? 0) > 0 || Boolean(getSelectionActions))
  const isServerMode = manualPagination || manualFiltering
  const rowAccentFor = getRowAccent ?? getRowStatus

  const [internalGlobalFilter, setInternalGlobalFilter] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [internalColumnFilters, setInternalColumnFilters] =
    useState<ColumnFiltersState>([])
  const [internalSorting, setInternalSorting] = useState<SortingState>([])
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>(
    () => getInitialColumnVisibility(columns),
  )
  const [internalColumnOrder, setInternalColumnOrder] = useState<string[]>(() =>
    columns.map((c) => c.id),
  )
  const [internalVisibleFilterIds, setInternalVisibleFilterIds] = useState<string[]>(() =>
    columns.filter((c) => c.filterType && c.showFilterByDefault !== false).map((c) => c.id),
  )
  const [internalColumnSizing, setInternalColumnSizing] = useState<ColumnSizingState>(() =>
    getDefaultColumnSizing(columns),
  )
  const [activeColumnDragId, setActiveColumnDragId] = useState<string | null>(null)
  const flexColumnIdAtDragStartRef = useRef<string | undefined>(undefined)
  const columnReorderWidthSnapshotRef = useRef<Record<string, number> | null>(
    null,
  )
  const layoutColumnWidthsRef = useRef<Record<string, number>>({})
  const flexColumnIdRef = useRef<string | undefined>(undefined)
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({})
  const rowSelection = controlledRowSelection ?? internalRowSelection
  const setRowSelection = useCallback(
    (updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater
      onRowSelectionChangeProp?.(next)
      if (controlledRowSelection === undefined) setInternalRowSelection(next)
    },
    [rowSelection, onRowSelectionChangeProp, controlledRowSelection],
  )

  // Clear row selection whenever the reset key changes (e.g. after a bulk mutation).
  // This prevents stale selected IDs from being re-submitted on the next bulk action.
  const prevSelectionResetKey = useRef(selectionResetKey)
  useEffect(() => {
    if (selectionResetKey === undefined) return
    if (selectionResetKey !== prevSelectionResetKey.current) {
      prevSelectionResetKey.current = selectionResetKey
      setRowSelection({})
    }
  }, [selectionResetKey])
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  })

  const globalFilter = controlledGlobalFilter ?? internalGlobalFilter
  const setGlobalFilter = useCallback(
    (value: string) => {
      onGlobalFilterChangeProp?.(value)
      if (controlledGlobalFilter === undefined) setInternalGlobalFilter(value)
    },
    [controlledGlobalFilter, onGlobalFilterChangeProp],
  )

  const columnFilters = controlledColumnFilters ?? internalColumnFilters
  const setColumnFilters: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      onColumnFiltersChangeProp?.(updater)
      if (controlledColumnFilters === undefined) {
        setInternalColumnFilters(updater)
      }
    },
    [controlledColumnFilters, onColumnFiltersChangeProp],
  )

  const sorting = controlledSorting ?? internalSorting
  const setSorting: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      onSortingChangeProp?.(updater)
      if (controlledSorting === undefined) {
        setInternalSorting(updater)
      }
    },
    [controlledSorting, onSortingChangeProp],
  )

  const pagination = controlledPagination ?? internalPagination
  const setPagination: OnChangeFn<PaginationState> = useCallback(
    (updater) => {
      onPaginationChangeProp?.(updater)
      if (controlledPagination === undefined) {
        setInternalPagination(updater)
      }
    },
    [controlledPagination, onPaginationChangeProp],
  )

  const columnVisibility = controlledColumnVisibility ?? internalColumnVisibility
  const setColumnVisibility: OnChangeFn<VisibilityState> = useCallback(
    (updater) => {
      onColumnVisibilityChangeProp?.(updater)
      if (controlledColumnVisibility === undefined) {
        setInternalColumnVisibility(updater)
      }
    },
    [controlledColumnVisibility, onColumnVisibilityChangeProp],
  )

  const columnOrder = controlledColumnOrder ?? internalColumnOrder
  const setColumnOrder: OnChangeFn<string[]> = useCallback(
    (updater) => {
      onColumnOrderChangeProp?.(updater)
      if (controlledColumnOrder === undefined) {
        setInternalColumnOrder(updater)
      }
    },
    [controlledColumnOrder, onColumnOrderChangeProp],
  )

  const visibleFilterIds = controlledVisibleFilterIds ?? internalVisibleFilterIds
  const setVisibleFilterIds = useCallback(
    (ids: string[]) => {
      onVisibleFilterIdsChangeProp?.(ids)
      if (controlledVisibleFilterIds === undefined) {
        setInternalVisibleFilterIds(ids)
      }
    },
    [controlledVisibleFilterIds, onVisibleFilterIdsChangeProp],
  )

  const orderedColumns = useMemo(
    () => orderColumnDefinitions(columns, columnOrder),
    [columns, columnOrder],
  )

  const visibleOrderedColumnIds = useMemo(
    () =>
      columnOrder.filter(
        (id) =>
          columnVisibility[id] !== false &&
          orderedColumns.some((c) => c.id === id),
      ),
    [columnOrder, columnVisibility, orderedColumns],
  )

  const rightmostVisibleColumnId =
    visibleOrderedColumnIds[visibleOrderedColumnIds.length - 1]

  /** Rightmost visible data column grows/shrinks to fill the table width. */
  const flexColumnId = rightmostVisibleColumnId

  const flexColumnDef = useMemo(
    () => orderedColumns.find((c) => c.id === flexColumnId),
    [orderedColumns, flexColumnId],
  )

  const columnDefs = useMemo(
    () =>
      buildDataColumnDefs(orderedColumns, rows, {
        rightmostVisibleColumnId: rightmostVisibleColumnId,
        flexColumnId,
      }),
    [orderedColumns, rows, rightmostVisibleColumnId, flexColumnId],
  )

  const allFilterColumns = useMemo(
    () =>
      allFilterColumnsProp ??
      orderedColumns.filter((c) => c.filterType),
    [allFilterColumnsProp, orderedColumns],
  )

  const filterableColumns = useMemo(() => {
    const resolved = resolveFilterableColumns(
      orderedColumns,
      visibleFilterIds,
      columnFilters,
    )
    if (!filterableColumnsProp) {
      return resolved
    }
    const byId = new Map(resolved.map((c) => [c.id, c]))
    for (const col of filterableColumnsProp) {
      byId.set(col.id, col)
    }
    const fromOrdered = orderedColumns
      .filter((c) => c.filterType && byId.has(c.id))
      .map((c) => byId.get(c.id)!)
    // Append virtual columns (in filterableColumnsProp but not in orderedColumns)
    const orderedIdSet = new Set(orderedColumns.map((c) => c.id))
    const virtual = filterableColumnsProp.filter((c) => !orderedIdSet.has(c.id))
    return [...fromOrdered, ...virtual]
  }, [
    filterableColumnsProp,
    orderedColumns,
    visibleFilterIds,
    columnFilters,
  ])

  const columnSizing = controlledColumnSizing ?? internalColumnSizing
  const setColumnSizing: OnChangeFn<ColumnSizingState> = useCallback(
    (updater) => {
      onColumnSizingChangeProp?.(updater)
      if (controlledColumnSizing === undefined) {
        setInternalColumnSizing(updater)
      }
    },
    [controlledColumnSizing, onColumnSizingChangeProp],
  )

  const columnDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  const chipFilters = columnFiltersToChipState(columnFilters)

  const table = useReactTable<TRow>({
    data: rows,
    columns: columnDefs,
    getRowId: (row) => row.id,
    state: {
      sorting,
      columnFilters,
      globalFilter: isServerMode ? undefined : globalFilter,
      columnVisibility,
      columnOrder,
      columnSizing,
      rowSelection,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: isServerMode ? undefined : setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    defaultColumn: { minSize: 60, maxSize: 1200, size: 150 },
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    ...(!isServerMode && {
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      globalFilterFn: globalSearchFilterFn<TRow>(),
    }),
    manualPagination: isServerMode && manualPagination,
    manualFiltering: isServerMode && manualFiltering,
    manualSorting: isServerMode,
    pageCount: isServerMode && manualPagination ? serverPageCount : undefined,
    enableRowSelection: showSelection
      ? (getRowCanSelect
          ? (row) => getRowCanSelect(row.original)
          : true)
      : false,
    enableMultiRowSelection,
    /** Cycle asc ↔ desc; third click must not clear sort (controlled prefs always have a sort). */
    enableSortingRemoval: false,
  })

  const visibleLeafColumns = table.getVisibleLeafColumns()
  const visibleColumnIds = visibleLeafColumns.map((c) => c.id)
  const controlsDisabled = loading || fetching

  const columnSizingInfo = table.getState().columnSizingInfo
  const isResizingColumn = Boolean(columnSizingInfo?.isResizingColumn)

  const tableClassName = [
    s.table,
    isResizingColumn ? s.tableResizing : '',
  ]
    .filter(Boolean)
    .join(' ')

  const activeDragColumn = activeColumnDragId
    ? visibleLeafColumns.find((c) => c.id === activeColumnDragId)
    : undefined
  const activeDragColDef = activeColumnDragId
    ? orderedColumns.find((c) => c.id === activeColumnDragId)
    : undefined
  const activeDragLabel =
    activeDragColDef != null
      ? getColumnLabel(activeDragColDef)
      : activeDragColumn?.id

  const renderColumnDragOverlay = () => (
    <DragOverlay dropAnimation={null}>
      {activeDragColumn && activeDragLabel ? (
        <div
          className={s.columnDragOverlay}
          style={{ width: activeDragColumn.getSize() }}
        >
          {activeDragLabel}
        </div>
      ) : null}
    </DragOverlay>
  )

  const wrapBodyWithFetchingOverlay = (content: React.ReactNode) => (
    <div className={s.tableBodyScrollHost}>
      {content}
      {fetching && (
        <div
          className={s.tableFetchingOverlay}
          aria-busy="true"
          aria-label={t("shared.table.loadingResults")}
        >
          <div className={s.tableFetchingSpinner} aria-hidden />
        </div>
      )}
    </div>
  )

  const pageRows = table.getRowModel().rows
  const filteredCount = isServerMode
    ? (serverRowCount ?? rows.length)
    : table.getFilteredRowModel().rows.length
  const pageCount = isServerMode
    ? (serverPageCount ?? 1)
    : table.getPageCount()
  const { pageIndex, pageSize } = table.getState().pagination

  const headerCheckRef = useRef<HTMLInputElement>(null)
  const headerScrollRef = useRef<HTMLDivElement>(null)
  const bodyScrollRef = useRef<HTMLDivElement>(null)
  const inlineScrollRef = useRef<HTMLDivElement>(null)
  const layoutMeasureRef = useRef<HTMLDivElement>(null)
  const [contentAreaWidth, setContentAreaWidth] = useState(0)
  const [resizeGuide, setResizeGuide] = useState<{
    top: number
    left: number
    height: number
  } | null>(null)
  /** Keeps ⠿ visible but non-draggable while a sort click is in flight. */
  const [reorderPausedForSort, setReorderPausedForSort] = useState(false)
  const allPageSelected = table.getIsAllPageRowsSelected()
  const somePageSelected = table.getIsSomePageRowsSelected()

  const syncHeaderScroll = useCallback(() => {
    const header = headerScrollRef.current
    const body = bodyScrollRef.current
    if (header && body) header.scrollLeft = body.scrollLeft
  }, [])

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = somePageSelected && !allPageSelected
    }
  })

  useEffect(() => {
    const target = layoutMeasureRef.current
    if (!target) return
    const observer = new ResizeObserver((entries) => {
      // Floor to avoid subpixel feedback loops (measure 800.4 → table 800.4 → parent 801 → …)
      const width = Math.floor(entries[0]?.contentRect.width ?? 0)
      setContentAreaWidth((prev) => (prev === width ? prev : width))
    })
    observer.observe(target)
    setContentAreaWidth(Math.floor(target.getBoundingClientRect().width))
    return () => observer.disconnect()
  }, [fillContainer, loading, filteredCount])

  useEffect(() => {
    if (!isResizingColumn) return
    const prevUserSelect = document.body.style.userSelect
    document.body.style.userSelect = 'none'
    return () => {
      document.body.style.userSelect = prevUserSelect
    }
  }, [isResizingColumn])

  const pauseReorderForSort = useCallback(() => {
    setReorderPausedForSort(true)
  }, [])

  const resumeReorderForSort = useCallback(() => {
    setReorderPausedForSort(false)
  }, [])

  useLayoutEffect(() => {
    if (!reorderPausedForSort) return
    let outer = 0
    let inner = 0
    outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setReorderPausedForSort(false))
    })
    return () => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
    }
  }, [sorting])

  useEffect(() => {
    if (!reorderPausedForSort) return
    const release = () => resumeReorderForSort()
    const timeout = window.setTimeout(release, 250)
    window.addEventListener('pointerup', release)
    window.addEventListener('pointercancel', release)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('pointerup', release)
      window.removeEventListener('pointercancel', release)
    }
  }, [reorderPausedForSort, resumeReorderForSort])

  const setChipFilter = useCallback(
    (id: string, vs: string[] | null) => {
      setColumnFilters((prev) => {
        const chips = columnFiltersToChipState(prev)
        if (vs && vs.length > 0) chips[id] = vs
        else delete chips[id]
        return chipStateToColumnFilters(chips)
      })
      setPagination((p) => ({ ...p, pageIndex: 0 }))
    },
    [setColumnFilters, setPagination],
  )

  const clearAllFilters = useCallback(() => {
    setColumnFilters([])
    setGlobalFilter('')
    setPagination((p) => ({ ...p, pageIndex: 0 }))
  }, [setColumnFilters, setGlobalFilter, setPagination])

  const hasActiveFilters = columnFilters.length > 0 || globalFilter.trim().length > 0

  const sortableColumns = useMemo(
    () => sortableColumnsProp ?? columns.filter((c) => c.sortable),
    [sortableColumnsProp, columns],
  )

  const derivedSort = useMemo(() => {
    if (sortProp) return sortProp
    const first = sorting[0]
    if (!first) return undefined
    const col = columns.find((c) => c.id === first.id)
    return {
      sort_by: col?.sortKey ?? first.id,
      order: first.desc ? ('desc' as const) : ('asc' as const),
    }
  }, [sortProp, sorting, columns])

  const handleSortChange = useCallback(
    (next: { sort_by: string; order: 'asc' | 'desc' }) => {
      if (onSortChangeProp) {
        onSortChangeProp(next)
        return
      }
      const col = columns.find((c) => (c.sortKey ?? c.id) === next.sort_by)
      setSorting([{ id: col?.id ?? next.sort_by, desc: next.order === 'desc' }])
    },
    [onSortChangeProp, columns, setSorting],
  )
  const searchTrimmed = globalFilter.trim()
  const resolvedEmptyTitle = searchTrimmed
    ? `${emptyTitle} matching “${searchTrimmed}”`
    : emptyTitle
  const emptyActionLabel = searchTrimmed
    ? 'Clear search'
    : hasActiveFilters
      ? 'Clear filters'
      : null
  const clearEmptyQuery = () => {
    if (searchTrimmed) {
      setGlobalFilter('')
      setPagination((p) => ({ ...p, pageIndex: 0 }))
      return
    }
    clearAllFilters()
  }
  const selectedIds = Object.keys(rowSelection)
  const effectiveSelectionActions = getSelectionActions
    ? getSelectionActions(selectedIds, rows)
    : selectionActions
  const showSelectionBar = (effectiveSelectionActions?.length ?? 0) > 0 && selectedIds.length > 0

  const renderSelectionBar = () =>
    showSelectionBar ? (
      <BulkSelectionBar
        selectedIds={selectedIds}
        actions={effectiveSelectionActions!}
        onClear={() => setRowSelection({})}
        compact={narrowChrome}
        getSingleName={
          getRowLabel
            ? () => {
                const r = rows.find((row) => row.id === selectedIds[0])
                return r ? getRowLabel(r) : undefined
              }
            : undefined
        }
      />
    ) : null

  const renderDataHeaders = (hg: ReturnType<typeof table.getHeaderGroups>[0]) => (
    <SortableContext items={visibleColumnIds} strategy={horizontalListSortingStrategy}>
      {hg.headers.map((header) => (
        <SortableHeaderCell
          key={header.id}
          header={header}
          className={s.th}
          showReorderHandle={enableColumnReorder}
          enableReorder={
            enableColumnReorder &&
            !isResizingColumn &&
            !reorderPausedForSort &&
            !controlsDisabled
          }
          isTableResizing={isResizingColumn}
          interactionDisabled={controlsDisabled}
          onSortPointerDown={pauseReorderForSort}
        />
      ))}
    </SortableContext>
  )

  const renderHeaderRow = (hg: ReturnType<typeof table.getHeaderGroups>[0]) => (
    <tr key={hg.id}>
      {showSelection && (
        <th className={`${s.th} ${s.thCheckbox}`}>
          <span className={s.checkboxWrap}>
            <input
              ref={headerCheckRef}
              type="checkbox"
              className={s.checkbox}
              checked={allPageSelected}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
            />
          </span>
        </th>
      )}
      {renderDataHeaders(hg)}
      {enableRowActions && <th className={`${s.th} ${s.thActions}`} />}
    </tr>
  )

  const { tableLayoutWidth, layoutColumnWidths } = useMemo(() => {
    const selectionW = showSelection
      ? (narrowChrome ? s.tableCheckboxColumnWidthNarrow : s.tableCheckboxColumnWidth)
      : 0
    // Must match <col> / thActions width — a mismatch makes the table a few px
    // wider than the measure and can reintroduce scrollbar ↔ ResizeObserver loops.
    const actionsW = enableRowActions ? 44 : 0
    const flexId = flexColumnDef?.id
    const flexMin = flexColumnDef
      ? (flexColumnDef.minWidth ?? flexColumnDef.width ?? 150)
      : 0

    const widths: Record<string, number> = {}
    let fixedSum = selectionW + actionsW

    for (const col of visibleLeafColumns) {
      if (col.id === flexId) continue
      const w = col.getSize()
      widths[col.id] = w
      fixedSum += w
    }

    const naturalTotal =
      visibleLeafColumns.reduce((sum, col) => sum + col.getSize(), 0) +
      selectionW +
      actionsW

    // Stretch the rightmost column to fill the measured container. When content
    // fits, use width:100% (not a pixel width equal to the measure) so the table
    // cannot push the ResizeObserver target wider and feedback-expand ancestors
    // that use overflow:visible (e.g. CollapsibleSection).
    if (flexId && contentAreaWidth > 0) {
      const minTableWidth = fixedSum + flexMin
      if (contentAreaWidth >= minTableWidth) {
        widths[flexId] = Math.max(flexMin, contentAreaWidth - fixedSum)
        return {
          tableLayoutWidth: '100%' as const,
          layoutColumnWidths: widths,
        }
      }
      widths[flexId] = flexMin
      return {
        tableLayoutWidth: minTableWidth,
        layoutColumnWidths: widths,
      }
    }

    for (const col of visibleLeafColumns) {
      widths[col.id] = col.getSize()
    }
    return {
      tableLayoutWidth: naturalTotal,
      layoutColumnWidths: widths,
    }
  }, [
    visibleLeafColumns,
    showSelection,
    enableRowActions,
    flexColumnDef,
    contentAreaWidth,
    columnSizing,
    columnSizingInfo.isResizingColumn,
    narrowChrome,
  ])

  const tableLayoutStyle = useMemo(
    () => ({ width: tableLayoutWidth }),
    [tableLayoutWidth],
  )

  layoutColumnWidthsRef.current = layoutColumnWidths
  flexColumnIdRef.current = flexColumnId

  const handleColumnDragStart = useCallback(
    (event: DragStartEvent) => {
      if (controlsDisabled) return
      if (table.getState().columnSizingInfo.isResizingColumn) return
      flexColumnIdAtDragStartRef.current = flexColumnIdRef.current
      columnReorderWidthSnapshotRef.current = {
        ...layoutColumnWidthsRef.current,
      }
      setActiveColumnDragId(String(event.active.id))
    },
    [controlsDisabled, table],
  )

  const handleColumnDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (controlsDisabled) return
      if (table.getState().columnSizingInfo.isResizingColumn) return

      const flexAtStart = flexColumnIdAtDragStartRef.current
      const snapshot = columnReorderWidthSnapshotRef.current
      const { active, over } = event

      if (enableColumnReorder) {
        createColumnDragEndHandler(() => visibleColumnIds, setColumnOrder)(event)
      }

      if (
        enableColumnReorder &&
        over &&
        flexAtStart &&
        snapshot &&
        (String(active.id) === flexAtStart || String(over.id) === flexAtStart)
      ) {
        setColumnSizing((prev) => {
          const next = { ...prev }
          for (const id of Object.keys(snapshot)) {
            const w = snapshot[id]
            if (w > 0) next[id] = Math.round(w)
          }
          return next
        })
      }

      flexColumnIdAtDragStartRef.current = undefined
      columnReorderWidthSnapshotRef.current = null
      setActiveColumnDragId(null)
    },
    [
      controlsDisabled,
      enableColumnReorder,
      visibleColumnIds,
      setColumnOrder,
      setColumnSizing,
      table,
    ],
  )

  const scrollEdgeDeps = [
    loading,
    filteredCount,
    pageIndex,
    pageSize,
    rows.length,
    columnVisibility,
    columnSizing,
    globalFilter,
    visibleLeafColumns.length,
    showSelection,
    enableRowActions,
    tableLayoutWidth,
    contentAreaWidth,
  ]

  const bodyScrollEdges = useScrollOverflowEdges(bodyScrollRef, scrollEdgeDeps)
  const inlineScrollEdges = useScrollOverflowEdges(inlineScrollRef, scrollEdgeDeps)

  const updateResizeGuide = useCallback(() => {
    const resizingId = columnSizingInfo.isResizingColumn
    if (!resizingId) {
      setResizeGuide(null)
      return
    }
    const root = fillContainer ? headerScrollRef.current : inlineScrollRef.current
    const cell = root?.querySelector(
      `[data-column-id="${String(resizingId)}"]`,
    ) as HTMLElement | null
    if (!cell) {
      setResizeGuide(null)
      return
    }
    const rect = cell.getBoundingClientRect()
    setResizeGuide({
      top: rect.top,
      left: rect.right,
      height: rect.height,
    })
  }, [columnSizingInfo.isResizingColumn, fillContainer])

  useLayoutEffect(() => {
    if (!isResizingColumn) {
      setResizeGuide(null)
      return
    }
    let rafId = 0
    const tick = () => {
      updateResizeGuide()
      rafId = requestAnimationFrame(tick)
    }
    tick()
    const root = fillContainer ? headerScrollRef.current : inlineScrollRef.current
    const ro = root ? new ResizeObserver(updateResizeGuide) : null
    if (root && ro) ro.observe(root)
    window.addEventListener('scroll', updateResizeGuide, true)
    window.addEventListener('resize', updateResizeGuide)
    return () => {
      cancelAnimationFrame(rafId)
      ro?.disconnect()
      window.removeEventListener('scroll', updateResizeGuide, true)
      window.removeEventListener('resize', updateResizeGuide)
    }
  }, [isResizingColumn, updateResizeGuide, fillContainer])

  useLayoutEffect(() => {
    const body = bodyScrollRef.current
    if (!body || body.scrollWidth <= body.clientWidth + 1) {
      if (body && body.scrollLeft !== 0) body.scrollLeft = 0
      const header = headerScrollRef.current
      if (header && header.scrollLeft !== 0) header.scrollLeft = 0
    }
  }, [tableLayoutWidth, contentAreaWidth, loading, filteredCount, fillContainer])

  const getColWidth = (columnId: string, fallback: number) =>
    layoutColumnWidths[columnId] ?? fallback

  const selectionColWidth = narrowChrome
    ? s.tableCheckboxColumnWidthNarrow
    : s.tableCheckboxColumnWidth

  const renderColGroup = () => (
    <colgroup>
      {showSelection && <col style={{ width: selectionColWidth }} />}
      {visibleLeafColumns.map((c) => (
        <col
          key={c.id}
          style={{ width: `${getColWidth(c.id, c.getSize())}px` }}
        />
      ))}
      {enableRowActions && <col style={{ width: 44 }} />}
    </colgroup>
  )

  const scrollClassName = fillContainer
    ? [
        s.tableScrollFill,
        !loading && filteredCount === 0 ? s.tableScrollEmptyFill : '',
      ]
        .filter(Boolean)
        .join(' ')
    : s.tableScroll

  const renderHeaderTable = () => (
    <DndContext
      sensors={columnDragSensors}
      collisionDetection={closestCenter}
      onDragStart={handleColumnDragStart}
      onDragEnd={handleColumnDragEnd}
    >
      <table className={tableClassName} style={tableLayoutStyle}>
        {renderColGroup()}
        <thead>
          {loading ? (
            <tr>
              {showSelection && <th className={`${s.th} ${s.thCheckbox}`} />}
              {visibleLeafColumns.map((col) => {
                const def = orderedColumns.find((c) => c.id === col.id)
                const label = def ? getColumnLabel(def) : col.id
                return (
                  <th key={col.id} className={s.th}>
                    {def?.headerLabelIcon ? (
                      <TableHeaderIcon name={def.headerLabelIcon} label={label} />
                    ) : (
                      label
                    )}
                  </th>
                )
              })}
              {enableRowActions && <th className={`${s.th} ${s.thActions}`} />}
            </tr>
          ) : (
            table.getHeaderGroups().map((hg) => renderHeaderRow(hg))
          )}
        </thead>
      </table>
      {!controlsDisabled && renderColumnDragOverlay()}
    </DndContext>
  )

  const renderBodyTable = () => {
    if (loading) {
      return (
        <table className={tableClassName} style={tableLayoutStyle}>
          {renderColGroup()}
          <tbody>
            {Array.from({ length: 8 }).map((_, i) => (
              <tr key={i} className={s.tr}>
                {showSelection && (
                  <td className={`${s.td} ${s.tdCheckbox}`}>
                    <span className={s.skeleton} style={{ width: 16, height: 16 }} />
                  </td>
                )}
                {visibleLeafColumns.map((col) => (
                  <td key={col.id} className={s.td}>
                    <span
                      className={s.skeleton}
                      style={{
                        width: col.id === 'name' ? 160 : 80,
                        height: 12,
                      }}
                    />
                  </td>
                ))}
                {enableRowActions && <td className={`${s.td} ${s.tdActions}`} />}
              </tr>
            ))}
          </tbody>
        </table>
      )
    }

    if (filteredCount === 0) {
      return wrapBodyWithFetchingOverlay(
        <div className={s.emptyState}>
          <div className={s.emptyIcon}>
            <Icon name={emptyIcon} size={28} />
          </div>
          <div className={s.emptyTitle}>{resolvedEmptyTitle}</div>
          {!searchTrimmed && emptySubtitle ? (
            <div className={s.emptySubtitle}>{emptySubtitle}</div>
          ) : null}
          {emptyActionLabel ? (
            <button type="button" className={s.clearBtn} onClick={clearEmptyQuery}>
              {emptyActionLabel}
            </button>
          ) : null}
        </div>,
      )
    }

    return wrapBodyWithFetchingOverlay(
      <table className={tableClassName} style={tableLayoutStyle}>
        {renderColGroup()}
        <tbody>
          {pageRows.map((row) => {
            const original = row.original
            const isSelected = row.getIsSelected()
            const canSelect = row.getCanSelect()
            const accentClass = resolveRowAccentClass(rowAccentFor?.(original))
            const isHighlighted = getRowHighlighted?.(original) ?? false
            const rowCtas = getRowCtas?.(original)
            const flashInfo = flashingRows?.get(original.id)
            return (
              <tr
                key={row.id}
                className={[
                  s.tr,
                  onRowClick ? s.trClickable : '',
                  isSelected ? s.trSelected : '',
                  !canSelect && showSelection ? s.trDisabled : '',
                  accentClass,
                  isHighlighted ? s.trHighlighted : '',
                  flashInfo ? s.trUpdateShimmer : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('input,button,a,label,select,textarea,[role="menuitem"]')) return
                  onRowClick?.(original)
                }}
              >
                {showSelection && (
                  <td
                    className={`${s.td} ${s.tdCheckbox}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className={s.checkboxWrap}>
                      <input
                        type="checkbox"
                        className={s.checkbox}
                        checked={isSelected}
                        disabled={!canSelect}
                        onChange={row.getToggleSelectedHandler()}
                      />
                    </span>
                  </td>
                )}
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={`${s.td}${flashInfo?.fields.has(cell.column.id) ? ` ${s.tdValueFlash}` : ''}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
                {enableRowActions && (
                  <td
                    className={`${s.td} ${s.tdActions}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {rowCtas?.length ? <RowCtaOverlay groups={rowCtas} /> : null}
                    {(() => {
                      const rowActions =
                        getRowActions?.(row.original) ?? selectionActions
                      if (!rowActions?.length) return null
                      return (
                        <RowActionMenu
                          rowId={row.id}
                          rowLabel={getRowLabel?.(row.original)}
                          actions={rowActions}
                        />
                      )
                    })()}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>,
    )
  }

  const tableContent = (
    <>
      {loading ? (
            <table className={tableClassName} style={tableLayoutStyle}>
              {renderColGroup()}
              <thead>
                <tr>
                  {showSelection && <th className={`${s.th} ${s.thCheckbox}`} />}
                  {visibleLeafColumns.map((col) => {
                    const def = orderedColumns.find((c) => c.id === col.id)
                    const label = def ? getColumnLabel(def) : col.id
                    return (
                      <th key={col.id} className={s.th}>
                        {def?.headerLabelIcon ? (
                          <TableHeaderIcon name={def.headerLabelIcon} label={label} />
                        ) : (
                          label
                        )}
                      </th>
                    )
                  })}
                  {enableRowActions && <th className={`${s.th} ${s.thActions}`} />}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className={s.tr}>
                    {showSelection && (
                      <td className={`${s.td} ${s.tdCheckbox}`}>
                        <span className={s.skeleton} style={{ width: 16, height: 16 }} />
                      </td>
                    )}
                    {visibleLeafColumns.map((col) => (
                      <td key={col.id} className={s.td}>
                        <span
                          className={s.skeleton}
                          style={{
                            width: col.id === 'name' ? 160 : 80,
                            height: 12,
                          }}
                        />
                      </td>
                    ))}
                    {enableRowActions && <td className={`${s.td} ${s.tdActions}`} />}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : filteredCount === 0 ? (
            wrapBodyWithFetchingOverlay(
              <div className={s.emptyState}>
                <div className={s.emptyIcon}>
                  <Icon name={emptyIcon} size={28} />
                </div>
                <div className={s.emptyTitle}>{resolvedEmptyTitle}</div>
                {!searchTrimmed && emptySubtitle ? (
                  <div className={s.emptySubtitle}>{emptySubtitle}</div>
                ) : null}
                {emptyActionLabel ? (
                  <button type="button" className={s.clearBtn} onClick={clearEmptyQuery}>
                    {emptyActionLabel}
                  </button>
                ) : null}
              </div>,
            )
          ) : (
            wrapBodyWithFetchingOverlay(
            <DndContext
              sensors={columnDragSensors}
              collisionDetection={closestCenter}
              onDragStart={handleColumnDragStart}
              onDragEnd={handleColumnDragEnd}
            >
              <table className={tableClassName} style={tableLayoutStyle}>
                {renderColGroup()}
                <thead>
                  {table.getHeaderGroups().map((hg) => renderHeaderRow(hg))}
                </thead>
                <tbody>
                  {pageRows.map((row) => {
                  const original = row.original
                  const isSelected = row.getIsSelected()
                  const canSelect = row.getCanSelect()
                  const accentClass = resolveRowAccentClass(rowAccentFor?.(original))
                  const isHighlighted = getRowHighlighted?.(original) ?? false
                  const rowCtas = getRowCtas?.(original)
                  const flashInfo = flashingRows?.get(original.id)
                  return (
                    <tr
                      key={row.id}
                      className={[
                        s.tr,
                        onRowClick ? s.trClickable : '',
                        isSelected ? s.trSelected : '',
                        !canSelect && showSelection ? s.trDisabled : '',
                        accentClass,
                        isHighlighted ? s.trHighlighted : '',
                        flashInfo ? s.trUpdateShimmer : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('input,button,a,label,select,textarea,[role="menuitem"]')) return
                        onRowClick?.(original)
                      }}
                    >
                      {showSelection && (
                        <td
                          className={`${s.td} ${s.tdCheckbox}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className={s.checkboxWrap}>
                            <input
                              type="checkbox"
                              className={s.checkbox}
                              checked={isSelected}
                              disabled={!canSelect}
                              onChange={row.getToggleSelectedHandler()}
                            />
                          </span>
                        </td>
                      )}
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={`${s.td}${flashInfo?.fields.has(cell.column.id) ? ` ${s.tdValueFlash}` : ''}`}
                          style={{ width: cell.column.getSize() }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                      {enableRowActions && (
                        <td
                          className={`${s.td} ${s.tdActions}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className={s.rowCtaWrap}>
                            {rowCtas?.length ? <RowCtaOverlay groups={rowCtas} /> : null}
                            {(() => {
                              const rowActions =
                                getRowActions?.(row.original) ?? selectionActions
                              if (!rowActions?.length) return null
                              return (
                                <RowActionMenu
                                  rowId={row.id}
                                  rowLabel={getRowLabel?.(row.original)}
                                  actions={rowActions}
                                />
                              )
                            })()}
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
              {!controlsDisabled && renderColumnDragOverlay()}
            </DndContext>,
            )
          )}
    </>
  )

  return (
    <>
      {resizeGuide && (
        <div
          className={s.columnResizeGuide}
          style={{
            top: resizeGuide.top,
            left: resizeGuide.left,
            height: resizeGuide.height,
          }}
          aria-hidden
        />
      )}
      <div
        className={[
          s.tableWrap,
          fillContainer ? s.tableWrapFill : s.tableWrapStack,
          isResizingColumn ? s.tableWrapResizing : '',
          narrowChrome ? s.tableWrapNarrow : '',
        ].join(' ')}
      >
        <div
          className={[
            s.toolbar,
            fillContainer
              ? narrowChrome
                ? s.toolbarFillCompact
                : s.toolbarFill
              : '',
            controlsDisabled ? s.tableControlsDisabled : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {narrowChrome ? (
            <div className={s.compactToolbar}>
              {tabs ? (
                <div className={s.compactToolbarRow}>
                  <div className={s.scopeControl} role="tablist">
                    {tabs.map((tab) => (
                      <button
                        key={tab.value}
                        type="button"
                        role="tab"
                        className={`${s.scopeBtn}${activeTab === tab.value ? ' is-active' : ''}`}
                        onClick={() => onTabChange?.(tab.value)}
                      >
                        {tab.label}
                        <span className={s.scopeCount}>{tab.count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className={s.compactToolbarRow}>
                <div
                  className={`${s.searchWrap} ${s.compactSearchWrap}`}
                  data-expanded="true"
                >
                  <span className={s.searchIcon}>
                    <Icon name="search" size={14} />
                  </span>
                  <input
                    ref={searchInputRef}
                    type="search"
                    className={s.searchInput}
                    placeholder={searchPlaceholder}
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value)
                      setPagination((p) => ({ ...p, pageIndex: 0 }))
                    }}
                    aria-label={searchPlaceholder}
                  />
                  {globalFilter.trim() ? (
                    <button
                      type="button"
                      className={s.searchClearBtn}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setGlobalFilter('')
                        setPagination((p) => ({ ...p, pageIndex: 0 }))
                        searchInputRef.current?.focus()
                      }}
                      aria-label={t('shared.filter.clearSearch')}
                      title={t('shared.filter.clearSearch')}
                    >
                      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  ) : null}
                </div>
              </div>

              <div className={s.compactToolbarRow}>
                <button
                  type="button"
                  className={s.compactFiltersBtn}
                  onClick={() => setFiltersSheetOpen(true)}
                >
                  <Icon name="filter" size={14} />
                  Quick Filters
                </button>
                {hasActiveFilters ? (
                  <button
                    type="button"
                    className={s.compactClearBtn}
                    onClick={clearAllFilters}
                    aria-label={t('shared.listView.clearAll')}
                    title={t('shared.listView.clearAll')}
                  >
                    <Icon name="x" size={14} />
                  </button>
                ) : null}
                {enableViewConfig ? (
                  <FiltersConfigButton
                    filterColumns={allFilterColumns}
                    visibleFilterIds={visibleFilterIds}
                    onVisibleFilterIdsChange={setVisibleFilterIds}
                    presentation="modal"
                  />
                ) : null}
                {enableViewConfig ? (
                  <ColumnsConfigButton
                    columns={columns}
                    columnVisibility={columnVisibility}
                    columnOrder={columnOrder}
                    onColumnVisibilityChange={setColumnVisibility}
                    onColumnOrderChange={(order) => setColumnOrder(order)}
                    presentation="modal"
                  />
                ) : null}
              </div>

              {(toolbarAbove || toolbarEnd) ? (
                <div className={s.compactToolbarRowBetween}>
                  {toolbarAbove}
                  {toolbarEnd}
                </div>
              ) : null}

              <FiltersSheetModal
                open={filtersSheetOpen}
                onOpenChange={setFiltersSheetOpen}
                allFilterColumns={allFilterColumns}
                visibleFilterIds={visibleFilterIds}
                columnFilters={columnFilters}
                rows={rows}
                onChipFilterChange={(colId, values) => setChipFilter(colId, values)}
                sortableColumns={sortableColumns}
                sort={derivedSort ?? undefined}
                onSortChange={handleSortChange}
                showCounts={!isServerMode}
              />
            </div>
          ) : (
          <div className={s.toolbarInner} data-listview-toolbar="">
            {tabs && (
              <>
                <div className={s.scopeControl} role="tablist">
                  {tabs.map((tab) => (
                    <button
                      key={tab.value}
                      type="button"
                      role="tab"
                      className={`${s.scopeBtn}${activeTab === tab.value ? ' is-active' : ''}`}
                      onClick={() => onTabChange?.(tab.value)}
                    >
                      {tab.label}
                      <span className={s.scopeCount}>{tab.count}</span>
                    </button>
                  ))}
                </div>
                <div className={s.toolbarSep} />
              </>
            )}

            <div
              className={s.searchWrap}
              data-expanded={
                searchFocused || globalFilter.trim().length > 0
                  ? 'true'
                  : undefined
              }
              onClick={() => searchInputRef.current?.focus()}
            >
              <span className={s.searchIcon}>
                <Icon name="search" size={14} />
              </span>
              <input
                ref={searchInputRef}
                type="search"
                className={s.searchInput}
                placeholder={searchPlaceholder}
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value)
                  setPagination((p) => ({ ...p, pageIndex: 0 }))
                }}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />
              {globalFilter.trim() ? (
                <button
                  type="button"
                  className={s.searchClearBtn}
                  onMouseDown={(e) => {
                    // Keep focus from leaving before clear so the field stays expanded
                    e.preventDefault()
                  }}
                  onClick={() => {
                    setGlobalFilter('')
                    setPagination((p) => ({ ...p, pageIndex: 0 }))
                    searchInputRef.current?.focus()
                  }}
                  aria-label={t("shared.filter.clearSearch")}
                  title={t("shared.filter.clearSearch")}
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              ) : null}
            </div>

            {filterableColumns.map((col) => (
              <FilterToolbarChip
                key={col.id}
                column={col}
                allRows={rows}
                value={chipFilters[col.id] ?? null}
                onChange={(vs) => setChipFilter(col.id, vs)}
                showCounts={!isServerMode}
              />
            ))}

            {enableViewConfig && (
              <FiltersConfigButton
                filterColumns={allFilterColumns}
                visibleFilterIds={visibleFilterIds}
                onVisibleFilterIdsChange={setVisibleFilterIds}
              />
            )}

            {enableViewConfig && (
              <ColumnsConfigButton
                columns={columns}
                columnVisibility={columnVisibility}
                columnOrder={columnOrder}
                onColumnVisibilityChange={setColumnVisibility}
                onColumnOrderChange={(order) => setColumnOrder(order)}
              />
            )}

            {hasActiveFilters && (
              <button type="button" className={s.clearBtn} onClick={clearAllFilters}>
                Clear all
              </button>
            )}

            <div className={s.toolbarSpacer} data-toolbar-spacer="" />

            {toolbarAbove}
            {toolbarEnd}
          </div>
          )}
        </div>

        {fillContainer ? (
          <div ref={layoutMeasureRef} className={s.tableBodyArea}>
            <div className={s.tableHeaderPane} ref={headerScrollRef}>
              {renderHeaderTable()}
            </div>
            <div className={`${s.scrollRegion} ${s.scrollRegionFill}`}>
              <ScrollOverflowFades
                edges={bodyScrollEdges}
                insetLeftFadeAfterSelection={showSelection}
                narrowSelectionColumn={narrowChrome}
              />
              <div
                ref={bodyScrollRef}
                className={scrollClassName}
                onScroll={syncHeaderScroll}
              >
                {renderBodyTable()}
              </div>
              {renderSelectionBar()}
            </div>
          </div>
        ) : (
          <div ref={layoutMeasureRef} className={s.scrollRegion}>
            <ScrollOverflowFades
              edges={inlineScrollEdges}
              insetLeftFadeAfterSelection={showSelection}
              narrowSelectionColumn={narrowChrome}
            />
            <div ref={inlineScrollRef} className={scrollClassName}>
              {tableContent}
            </div>
            {renderSelectionBar()}
          </div>
        )}

        {!loading && filteredCount > 0 && (
          <div
            className={controlsDisabled ? s.tableControlsDisabled : undefined}
          >
            <Pagination
              className={
                fillContainer
                  ? narrowChrome
                    ? s.paginationFillCompact
                    : s.paginationFill
                  : undefined
              }
              page={pageIndex}
              pageCount={pageCount}
              pageSize={pageSize}
              total={filteredCount}
              onPage={(p) => table.setPageIndex(p)}
              onPageSize={(n) => {
                table.setPageSize(n)
                table.setPageIndex(0)
              }}
              center={paginationCenter}
              compact={narrowChrome}
            />
          </div>
        )}

        {/* View switcher (etc.) when there are no rows and thus no pagination bar */}
        {!loading && filteredCount === 0 && paginationCenter ? (
          narrowChrome ? (
            <div
              className={[
                s.paginationCompact,
                fillContainer ? s.paginationFillCompact : undefined,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div className={s.paginationCompactInner}>
                <div className={s.paginationCompactRow}>{paginationCenter}</div>
              </div>
            </div>
          ) : (
          <div className={[s.pagination, fillContainer ? s.paginationFill : undefined].filter(Boolean).join(' ')}>
            <div className={s.paginationInfo} />
            <div className={s.paginationCenter}>{paginationCenter}</div>
            <div className={s.paginationEnd} />
          </div>
          )
        ) : null}

        {footer ? <div className={s.tableFooter}>{footer}</div> : null}
      </div>

    </>
  )
}
